import { useState, useRef, useEffect } from "react";
import { FiChevronDown, FiX, FiCheck, FiSearch } from "react-icons/fi";
import { api } from "../services/api";

interface Customer {
  id: string;
  name: string;
}

interface MultiSelectCustomerSearchProps {
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  endpoint?: string; // Endpoint customizado para carregar clientes
}

// Função para remover acentos
function removeAccents(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export function MultiSelectCustomerSearch({
  value,
  onChange,
  placeholder = "Buscar clientes...",
  endpoint = "/costumers",
}: MultiSelectCustomerSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Carregar todos os clientes ao montar
  useEffect(() => {
    loadCustomers();
  }, [endpoint]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const response = await api.get(endpoint);
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar clientes quando o searchTerm mudar
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCustomers(customers);
      return;
    }

    const normalizedSearch = removeAccents(searchTerm);
    const filtered = customers.filter(customer =>
      removeAccents(customer.name).includes(normalizedSearch),
    );
    setFilteredCustomers(filtered);
  }, [searchTerm, customers]);

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleCustomer = (customerId: string) => {
    if (value.includes(customerId)) {
      onChange(value.filter(id => id !== customerId));
    } else {
      onChange([...value, customerId]);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  const getSelectedLabels = () => {
    return customers
      .filter(customer => value.includes(customer.id))
      .map(customer => customer.name);
  };

  const renderSelectedDisplay = () => {
    if (value.length === 0) {
      return <span className="text-gray-400 text-sm">{placeholder}</span>;
    }

    if (value.length <= 2) {
      return (
        <div className="flex items-center gap-1 flex-wrap">
          {getSelectedLabels().map(label => (
            <span
              key={label}
              className="px-2 py-0.5 bg-[#0085BD]/10 text-[#0085BD] text-xs rounded-full truncate max-w-[150px]"
            >
              {label}
            </span>
          ))}
        </div>
      );
    }

    return (
      <span className="text-sm text-gray-700">
        {value.length}{" "}
        {value.length === 1 ? "cliente selecionado" : "clientes selecionados"}
      </span>
    );
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Botão principal */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-[52px] px-4 bg-white border rounded-lg flex items-center justify-between transition-colors ${
          isOpen
            ? "border-[#0085BD] ring-1 ring-[#0085BD]"
            : "border-gray-200 hover:border-[#0085BD]"
        }`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
          {renderSelectedDisplay()}
        </div>
        <div className="flex items-center gap-2">
          {value.length > 0 && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                clearAll();
              }}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="Limpar seleção"
            >
              <FiX className="text-gray-400" size={14} />
            </button>
          )}
          <FiChevronDown
            className={`text-gray-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
            size={18}
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Campo de busca */}
          <div className="p-3 border-b border-gray-100 bg-gray-50">
            <div className="relative">
              <FiSearch
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Buscar cliente..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] outline-none"
                autoFocus
              />
            </div>
          </div>

          {/* Lista de clientes */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Carregando clientes...
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Nenhum cliente encontrado
              </div>
            ) : (
              <>
                {value.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 border-b border-gray-100 flex items-center gap-2 sticky top-0 bg-white z-10"
                  >
                    <FiX size={14} />
                    Limpar seleção ({value.length})
                  </button>
                )}
                {filteredCustomers.map(customer => (
                  <button
                    key={customer.id}
                    type="button"
                    onClick={() => toggleCustomer(customer.id)}
                    className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                      value.includes(customer.id) ? "bg-blue-50" : ""
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                        value.includes(customer.id)
                          ? "border-[#0085BD] bg-[#0085BD]"
                          : "border-gray-300"
                      }`}
                    >
                      {value.includes(customer.id) && (
                        <FiCheck className="text-white" size={12} />
                      )}
                    </div>
                    <span className="text-gray-700 truncate">
                      {customer.name}
                    </span>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
